<script setup lang="ts">
import RlImageThumb from '@/components/common/RlImageThumb.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { usePartDetails } from '@/composables/rc/part-details'
import { NotifyFail } from '@/helpers/core/notifications'
import type { IFailure } from '@/types/rc/miners'
import { ref } from 'vue'

const { minerId, selectedPart } = useRepairsContext()
const { isReplacement, failureId, hasRepairPictures, repairPictures, movedToInventory } = usePartDetails(
  minerId,
  selectedPart
)

/** Buffer to keep a reference to the picture being deleted (for loading state) */
const pictureOnDeletionState = ref<number>()

const { addFailurePicturesMutation, deleteFailurePicturesMutation } = useMinerRepairActions(minerId)

const { mutate: mutateUploadPictures, isPending: isPictureUploadingPending } = addFailurePicturesMutation

const { mutate: mutateDeletePictures } = deleteFailurePicturesMutation

const fileInput = ref()

const handleFailurePictureUpload = (e: Event) => {
  if (!failureId.value) return
  const target = e.target as HTMLInputElement

  const files = Array.from(target.files || [])

  const validFiles = files.filter((file) => {
    const isValidType = ['image/jpeg', 'image/png'].includes(file.type)
    const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
    return isValidType && isValidSize
  })

  if (validFiles.length < files.length) {
    NotifyFail('Invalid files selected, only png/jpeg images up to 10MB')
    return
  }

  mutateUploadPictures(
    { failure_id: failureId.value, pictures: validFiles },
    {
      onSettled: () => {
        fileInput.value.value = null
      },
    }
  )
}

const handleFailurePictureDeletion = (picture: IFailure['pictures'][number]) => {
  if (!failureId.value) return
  pictureOnDeletionState.value = picture.id
  mutateDeletePictures({ failure_id: failureId.value, pictures: [picture.id] })
}
</script>

<template>
  <div class="flex flex-col gap-spacing-sm">
    <el-text class="text-text-tertiary w-full">Images</el-text>
    <div v-if="hasRepairPictures" class="grid grid-cols-3 gap-4 w-full">
      <RlImageThumb
        v-for="picture in repairPictures"
        :key="picture.picture_url"
        :url="picture.picture_url"
        :deletable="true"
        :delete-pending="pictureOnDeletionState == picture.id"
        class-name="w-24 h-24"
        @delete="handleFailurePictureDeletion(picture)"
      />
    </div>
    <input
      ref="fileInput"
      class="hidden"
      type="file"
      accept=".jpg,.jpeg,.png"
      multiple
      @change="handleFailurePictureUpload"
    />
    <el-button
      class="bg-fill-primary w-max px-4 py-3 text-text-secondary"
      :loading="isPictureUploadingPending"
      :disabled="isPictureUploadingPending || isReplacement || movedToInventory"
      @click="fileInput.click()"
      >Upload image(s)</el-button
    >
  </div>
</template>
